/**
 * @module builder
 */
import type { QuoteDocument, TieredPackages, TieredPackageTierId } from 'paintscout'
// Deep imports, not the `../../../calculator` barrel: this module is reached from both the builder
// and the calculator, and the barrel closes a cycle back into the builder. Same hazard
// get-tier-prices documents.
import { parseNumber } from '../../../calculator/util/parse-number'
import { resolveTierPrice } from '../../../calculator/util/resolve-tier-price'
import { roundPrice } from '../../../calculator/util/round-price'
import { hasTierProductSwaps } from './swap-tier-products'

/**
 * One place that decides what a tier costs, so the drawer, the card list, the customer-facing
 * cards and the calculator never disagree.
 *
 * Three sources, in order:
 *
 * 1. The base tier is the estimate as built — always the base price, never adjustable.
 * 2. An overridden tier prices from its own `adjustment` (a percent off the base estimate, or an
 *    absolute dollar target). The estimator typed it, so it beats the product swap.
 * 3. Otherwise the tier prices itself: `resolvedPrice` when that tier swaps products, and the base
 *    price when it does not.
 *
 * "When it swaps products" is a per-tier question, asked with hasTierProductSwaps in both places a
 * stamped price could be read. A set can hold rules that move nothing for a given tier, and such a
 * tier must price off the live base like any other (ENG-1795).
 */

/**
 * The price of the estimate as built, in the same units as a tier price.
 *
 * `quote.totals.price` is the subtotal of the quote as it currently stands, and is the answer
 * whenever the quote sits on its base tier — a tier's delta is a separate total row, so the
 * subtotal is the base estimate. It stops being the answer only when the quote sits on another
 * tier *and* product rules moved the substrates: the subtotal then follows that tier, and the base
 * tier's stamped `resolvedPrice` is the only record of what the estimate as built came to.
 *
 * That stamped price is written once, when a template is applied or a tier is explicitly switched
 * (withResolvedTierPrices) — nothing re-stamps it as the estimate is subsequently built. So without
 * the product-swap check, a plain percentage/dollar package (the common case, no swap at all) would
 * read this stale snapshot for its whole editing session merely for being selected on a non-base
 * tier, deaf to every product or option added afterwards (ENG-1795, ENG-1799).
 */
export function getTieredPackageBasePrice(args: { quote: QuoteDocument }): number {
  const { quote } = args
  const subTotal = parseNumber(quote?.totals?.price)
  const tieredPackages = quote?.tieredPackages

  if (
    !tieredPackages ||
    tieredPackages.selectedTierId === tieredPackages.baseTierId ||
    !hasTierProductSwaps({ tieredPackages, tierId: tieredPackages.selectedTierId })
  ) {
    return roundPrice(subTotal)
  }

  const baseResolved = tieredPackages.tiers?.[tieredPackages.baseTierId]?.resolvedPrice

  return roundPrice(typeof baseResolved === 'number' ? baseResolved : subTotal)
}

/** Whether the tier's stored price came from the estimator rather than from the tier pricing itself. */
export function isTierOverridden(args: { tieredPackages?: TieredPackages; tierId: TieredPackageTierId }): boolean {
  const { tieredPackages, tierId } = args

  return !!tieredPackages && tierId !== tieredPackages.baseTierId && !!tieredPackages.tiers?.[tierId]?.overridden
}

/**
 * Whether a tier's typed dollar price no longer matches the estimate it was typed against.
 *
 * A `'value'` override is an absolute target and holds against work added afterwards — that is
 * deliberate, and `tierAdjustment` absorbs the difference so the customer-facing total does not
 * move. The hazard is that it holds silently: move a $2,000 item out of the options and into the
 * bid table and the package still bills the figure typed against the smaller estimate, with the
 * $2,000 given away. Nothing in the numbers shows it, since the typed figure looks the same either
 * way. So the estimator gets told (ENG-1843).
 *
 * Only dollar overrides can drift. A percentage override re-prices off the live base on every
 * render, so it is never stale, and an un-overridden tier prices off the base or its product swap.
 *
 * Returns false when `overrideBasePrice` is absent rather than guessing: an override typed before
 * that field existed has no recorded starting point, and a warning we cannot substantiate is worse
 * than none.
 */
export function isTierPriceStale(args: {
  tieredPackages?: TieredPackages
  tierId: TieredPackageTierId
  basePrice: number
}): boolean {
  const { tieredPackages, tierId, basePrice } = args

  const tier = tieredPackages?.tiers?.[tierId]

  if (
    !tier?.overridden ||
    tier.adjustment?.type !== 'value' ||
    tierId === tieredPackages?.baseTierId ||
    typeof tier.overrideBasePrice !== 'number'
  ) {
    return false
  }

  // A non-positive target is "no adjustment" to resolveTierPrice — the tier falls back to the live
  // base and tracks it like any other. Nothing is being held, so nothing can have gone stale.
  if (parseNumber(tier.adjustment.value) <= 0) {
    return false
  }

  return roundPrice(tier.overrideBasePrice) !== roundPrice(basePrice)
}

/** The resolved total for one tier. See the module comment for the order of precedence. */
export function resolveTieredPackageTierPrice(args: {
  tieredPackages?: TieredPackages
  tierId: TieredPackageTierId
  basePrice: number
}): number {
  const { tieredPackages, tierId, basePrice } = args

  if (!tieredPackages?.tiers || tierId === tieredPackages.baseTierId) {
    return roundPrice(basePrice)
  }

  const tier = tieredPackages.tiers[tierId]

  if (tier?.overridden) {
    return roundPrice(resolveTierPrice(basePrice, tier.adjustment))
  }

  /**
   * The stamp is only the answer for a tier that actually swaps products — the one case the live
   * subtotal cannot describe, since the substrates under that tier differ from the estimate as
   * built. Every other tier prices off the live base, stamp or no stamp.
   *
   * The guard matters because withResolvedTierPrices stamps all three tiers whenever the package
   * set has resolvable rules, without asking whether any rule moves a product for a given tier. A
   * template whose rules swap nothing between tiers therefore stamps three prices taken from the
   * estimate as it stood when the template was applied — $0 on a fresh estimate — and without this
   * check those tiers would keep reading that $0 for the rest of the editing session, while the
   * base tier (already guarded, in getTieredPackageBasePrice) tracked the estimate correctly.
   * Switching tiers re-stamped and hid the problem, which is exactly how it was reported (ENG-1795).
   */
  if (typeof tier?.resolvedPrice === 'number' && hasTierProductSwaps({ tieredPackages, tierId })) {
    return roundPrice(tier.resolvedPrice)
  }

  return roundPrice(basePrice)
}

/**
 * How much a tier's percentage override moves the estimate's optional items.
 *
 * Optional items hold their price by default — an upgrade the customer has not bought yet should
 * not silently reprice — so this is zero unless the tier is overridden by percentage with
 * `applyToOptions` on. `optionsTotal` is the optional-item money already on the quote
 * (`totals.addedOptions`), which the caller reads because the field layout differs by consumer.
 */
export function resolveTieredPackageOptionsAdjustment(args: {
  tieredPackages?: TieredPackages
  tierId: TieredPackageTierId
  optionsTotal: number
}): number {
  const { tieredPackages, tierId, optionsTotal } = args

  const tier = tieredPackages?.tiers?.[tierId]

  if (
    !tier?.overridden ||
    !tier.applyToOptions ||
    tier.adjustment?.type !== 'percentage' ||
    tierId === tieredPackages?.baseTierId
  ) {
    return 0
  }

  return roundPrice(optionsTotal * parseNumber(tier.adjustment.value) * 0.01)
}
